/* eslint-disable max-lines */

import _ from "lodash";
import { getEanForColorAndSize, getSeasonForProduct } from "./products";
import {
  getPriceForColorAndSizeAndGroup,
  getPriceForEanAndGroup,
} from "./productsPrices";
import { productNr } from "../utils/products";
import { getCustomerForId } from "./customers";

export const getCurrentOrderId = (context) =>
  _.get(context, "state.currentOrderId");

export const getOrderForId = (context) => (id) => {
  const orders = _.get(context, "entities.orders");
  if (!orders || !id) return null;
  return context.entities.orders.find((c) => c.id === id);
};

export const getCurrentOrder = (context) => {
  const currentOrderId = getCurrentOrderId(context);

  return getOrderForId(context)(currentOrderId);
};

export const isProductInCurrentOrder = (context) => (product) => {
  const order = getCurrentOrder(context);
  if (!order) return false;
  const { products } = order.doc;
  if (!products) return false;

  const findProduct = _.values(products).find(
    (p) => p.number === productNr(product),
  );
  return !!findProduct;
};

export const getCustomerForOrder = (context, order) => {
  if (order) {
    const customerId = _.get(order, "doc.customerId");

    return getCustomerForId(context, customerId);
  }

  return null;
};

export const getCurrentOrderCustomer = (context) => {
  const order = getCurrentOrder(context);

  return getCustomerForOrder(context, order);
};

export const getCustomerPriceGroups = (context) =>
  _.get(context, "entities.customerPriceGroups");

export const getCustomerPriceGroup = (context, customer) => {
  const priceGroups = getCustomerPriceGroups(context);

  if (!customer || !priceGroups) return null;

  const priceGroupId = customer.CustomerPriceGroup__c;

  return priceGroups[priceGroupId];
};

export const getCurrentOrderCustomerPriceGroup = (context) => {
  const currentCustomer = getCurrentOrderCustomer(context);
  return getCustomerPriceGroup(context, currentCustomer);
};

export const getOrders = (context) => _.get(context, "entities.orders");

export const getSyncProgress = (context) =>
  _.get(context, "state.sync.progress");

export const getSyncText = (context) => _.get(context, "state.sync.text");

const sumSizesForColor = (number, data, pricesForNameColorAndSize, season) => {
  let total = 0;
  if (data) {
    _.keys(data).forEach((color) => {
      if (!color) return;
      _.keys(data[color]).forEach((size) => {
        const price = pricesForNameColorAndSize({
          color,
          size,
          number,
          season,
        });
        total += data[color][size] * price;
      });
    });
  }

  return total;
};

export const mapDataWithLenghtsToDataWithColors = (data) => {
  const valuesForLength = _.values(data);
  let object = {};

  valuesForLength.forEach((k) => {
    _.keys(k).forEach((c) => {
      if (object[c]) {
        object = { ...object, [c]: { ...object[c], ...k[c] } };
      } else {
        object = { ...object, [c]: k[c] };
      }
    });
  });
  return object;
};

export const getTotalPriceForOrder = (order, pricesForNameColorAndSize) => {
  let total = 0;

  if (order && order.products) {
    let productIndex = 0;
    while (productIndex <= _.size(order.products) - 1) {
      const product = _.nth(_.values(order.products), productIndex);
      if (product) {
        total += sumSizesForColor(
          product.number,
          mapDataWithLenghtsToDataWithColors(product.data),
          pricesForNameColorAndSize,
          product.season,
        );
      }
      productIndex += 1;
    }
  }

  return total;
};

export const getTotalPrice = (context, order, filterColor, productNumber) => {
  let currency;
  const customer = getCustomerForOrder(context, order);
  const priceGroup = getCustomerPriceGroup(context, customer);
  if (!order) return {};

  const pricesForNameColorAndSize = ({ color, size, number, season }) => {
    if (filterColor && filterColor !== color) return 0;
    if (productNumber && productNumber !== number) return 0;
    const price = getPriceForColorAndSizeAndGroup({
      context,
      priceGroup,
      color,
      size,
      number,
      season,
    });
    if (!currency && price) {
      // eslint-disable-next-line prefer-destructuring
      currency = price.currency;
    }
    return price && price.value ? price.value : 0;
  };

  const total =
    getTotalPriceForOrder(order.doc, pricesForNameColorAndSize) || 0;

  const discount = 1 - (order.doc.discount || 0) / 100;

  return {
    total,
    currency,
    discount,
    totalWithDiscount: total * discount,
  };
};

export const getLineItemsForOrderBase = (
  product,
  context,
  eanForColorAndSize,
  priceForEanAndGroup,
) => {
  if (!product || !context || !eanForColorAndSize || !priceForEanAndGroup) {
    return null;
  }
  const { data } = product;

  const colors = _.keys(data);
  const lineItems = [];

  if (colors) {
    colors.forEach((col) => {
      const c = col;
      const sizes = _.keys(data[c]);

      sizes.forEach((s) => {
        const quantity = data[c][s];
        if (!quantity) return;
        const ean = eanForColorAndSize(c, s);
        lineItems.push({
          quantity: data[c][s],
          ean,
          price: priceForEanAndGroup(ean),
          deliveryPeriodCode: _.get(product, "deliveryDate.value"),
          number: product.number,
          season: product.season,
          mask_line_id: product.id,
        });
      });
    });
  }

  return lineItems;
};

export const getLineItemsForOrder = (product, context) => {
  const eanForColorAndSize = (color, size) =>
    getEanForColorAndSize({
      context,
      number: product.number,
      color,
      size,
    });
  const priceGroup = getCurrentOrderCustomerPriceGroup(context);
  const priceType = "u";

  const priceForEanAndGroup = (ean) =>
    getPriceForEanAndGroup({
      context,
      ean,
      priceGroup,
      priceType,
      number: product.number,
      season: product.season,
    });

  return _.compact(
    _.flatten(
      getLineItemsForOrderBase(
        product,
        context,
        eanForColorAndSize,
        priceForEanAndGroup,
      ),
    ),
  );
};

export const getPriceForSizeForProductAndColor = (
  sizes,
  actions,
  context,
  color,
  product,
  currentOrder,
) => {
  if (!color || !currentOrder) return {};
  const pricesForSizes = {};

  let currency = null;
  sizes.forEach((size) => {
    const unitPrice = getPriceForColorAndSizeAndGroup({
      context: { actions, ...context },
      color: color,
      number: product.number,
      size,
      priceGroup: getCurrentOrderCustomerPriceGroup(context),
      priceType: "u",
      season: getSeasonForProduct({
        context,
        orderSeason: currentOrder.doc.season,
        number: product.number,
      }),
    });

    const msrpPrice = getPriceForColorAndSizeAndGroup({
      context: { actions, ...context },
      color: color,
      number: product.number,
      size,
      priceGroup: getCurrentOrderCustomerPriceGroup(context),
      priceType: "m",
      season: getSeasonForProduct({
        context,
        orderSeason: currentOrder.doc.season,
        number: product.number,
      }),
    });
    if (unitPrice.value) {
      pricesForSizes[String(size)] = {
        unitPrice: unitPrice.value,
        msrpPrice: msrpPrice.value,
      };
    }
    if (!currency) {
      // eslint-disable-next-line prefer-destructuring
      currency = unitPrice.currency;
    }
  });
  return { pricesForSizes, currency };
};
